/**
 * Content Schema 工具函数
 *
 * 提供统一的 Content Schema 解析和操作方法
 * ONLY supports Content Schema format
 */

import { isTickerNodeType, NODE_TYPE_ENTITY, NODE_TYPE_INVESTMENT } from "@/constants/lexical";

interface ContentSchemaNodeLike {
  type?: string;
  symbol?: string;
  ticker?: string;
  tag?: string;
  children?: unknown[];
}

function asNodeLike(value: unknown): ContentSchemaNodeLike | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  return value as ContentSchemaNodeLike;
}

/**
 * 从 Content Schema 中提取所有 ticker symbols
 *
 * 支持的节点类型：
 * - StockNode (symbol 字段)
 * - SelfContractNode (ticker 字段)
 * - DecisionNode (ticker 字段)
 *
 * Content Schema format: { type: "root", version: 1, children: [...] }
 *
 * @param contentSchemaJson - Editor content (JSON string, Content Schema format)
 * @returns ticker symbols 数组（去重、大写）
 */
export function extractTickerSymbols(contentSchemaJson: string): string[] {
  if (!contentSchemaJson) {
    return [];
  }

  try {
    const contentSchema = JSON.parse(contentSchemaJson);

    // Content Schema: { type: "root", version: 1, children: [...] }
    if (contentSchema.type !== "root") {
      return [];
    }

    const symbols = new Set<string>();

    // 遍历所有节点
    traverseNodes(contentSchema, (node) => {
      // Content Schema format: Fields are directly on node object (no "data" wrapper)
      const nodeData = node;

      // 跳过没有 type 的节点（如 root 节点）
      if (!nodeData.type) {
        return;
      }

      // 检查是否是包含 ticker 的节点类型
      if (!isTickerNodeType(nodeData.type)) {
        return;
      }

      // 根据节点类型提取 ticker symbol
      const symbol = extractSymbolFromNode(nodeData);
      if (symbol) {
        symbols.add(symbol.toUpperCase());
      }
    });

    return Array.from(symbols);
  } catch (error) {
    console.error("[lexical-utils] Failed to extract ticker symbols:", error);
    return [];
  }
}

/**
 * 从节点中提取 ticker symbol
 * @param nodeData - Content Schema 节点数据（已经提取了 data 字段）
 * @returns ticker symbol 或 null
 */
function extractSymbolFromNode(nodeData: ContentSchemaNodeLike): string | null {
  switch (nodeData.type) {
    case NODE_TYPE_ENTITY.STOCK:
      return nodeData.symbol || null;

    case NODE_TYPE_INVESTMENT.SELF_CONTRACT:
      return nodeData.ticker || null;

    case NODE_TYPE_INVESTMENT.DECISION:
      return nodeData.ticker || null;

    default:
      return null;
  }
}

/**
 * 遍历 Content Schema 节点树（递归）
 * @param node - 当前节点
 * @param visitor - 访问者函数，对每个节点执行
 */
function traverseNodes(node: unknown, visitor: (node: ContentSchemaNodeLike) => void): void {
  const nodeLike = asNodeLike(node);
  if (!nodeLike) return;

  // 访问当前节点
  visitor(nodeLike);

  // Content Schema format: children are in node.children
  const children = nodeLike.children;

  // 遍历子节点
  if (children && Array.isArray(children)) {
    children.forEach((child) => traverseNodes(child, visitor));
  }
}

/**
 * 从 Content Schema 中提取所有 tags
 *
 * @param contentSchemaJson - Editor content (JSON string, Content Schema format)
 * @returns tags 数组（去重）
 */
export function extractTags(contentSchemaJson: string): string[] {
  if (!contentSchemaJson) {
    return [];
  }

  try {
    const contentSchema = JSON.parse(contentSchemaJson);

    // Content Schema: { type: "root", version: 1, children: [...] }
    if (contentSchema.type !== "root") {
      return [];
    }

    const tags = new Set<string>();

    traverseNodes(contentSchema, (node) => {
      // Content Schema format: Fields are directly on node object (no "data" wrapper)
      const nodeData = node;

      if (nodeData.type === NODE_TYPE_ENTITY.TAG && nodeData.tag) {
        tags.add(nodeData.tag);
      }
    });

    return Array.from(tags);
  } catch (error) {
    console.error("[lexical-utils] Failed to extract tags:", error);
    return [];
  }
}
